import React from 'react'

import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment-timezone'
import isFunction from 'lodash/isFunction'

import MaterialTimePicker from 'material-ui/TimePicker'
import DatePicker from 'react-datepicker'

import FormControl from './FormControl'

import 'react-datepicker/dist/react-datepicker.css'
import 'rc-time-picker/assets/index.css'

export default class DatePickerInput extends FormControl {
  static propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    placeholder: PropTypes.string,
    className: PropTypes.string,
    helpText: PropTypes.string,
    initialValue: PropTypes.any,
    filterDate: PropTypes.func,
    disabled: PropTypes.bool,
    clearable: PropTypes.bool,
    selectsStart: PropTypes.bool,
    selectsEnd: PropTypes.bool,
    selectDarkSkin: PropTypes.bool,
    selectLightFormSkin: PropTypes.bool,
    validationFuncs: PropTypes.array,
    onBlurExternal: PropTypes.func,
    dateFormat: PropTypes.string,
    minDate: PropTypes.object,
    maxDate: PropTypes.object,
    enableTime: PropTypes.bool,
    timeFormat: PropTypes.string,
    use12Hours: PropTypes.bool,
    showMonthDropdown: PropTypes.bool,
    showYearDropdown: PropTypes.bool,
    wrapperClass: PropTypes.string
  }

  static defaultProps = {
    disabled: false,
    clearable: false,
    selectsStart: false,
    selectsEnd: false,
    validationFuncs: [],
    initialValue: '',
    selectDarkSkin: false,
    dateFormat: 'DD/MM/YYYY',
    timeFormat: 'hh:mm:ss A',
    selectLightFormSkin: false,
    enableTime: false,
    use12Hours: true
  }

  handleBlur = () => {
    this.validate()

    if (isFunction(this.props.onBlurExternal)) {
      this.props.onBlurExternal()
    }
  }

  getDefaultState() {
    return {
      value: null
    }
  }

  getValue() {
    return this.state.value
  }

  renderStartDate() {
    const {
      id,
      clearable,
      placeholder,
      className,
      disabled,
      endDate,
      filterDate,
      minDate,
      maxDate,
      dateFormat,
      enableTime,
      timeFormat,
      use12Hours,
      showMonthDropdown,
      showYearDropdown
    } = this.props

    const dateTimePickerClass = classNames({
      'date-time-picker-wrapper': enableTime
    })

    return (
      <div className={dateTimePickerClass}>
        <DatePicker
          name={id}
          ref={id}
          selectsStart
          startDate={this.state.value}
          endDate={endDate}
          selected={this.state.value}
          onChange={this.onDateChange}
          onBlur={this.handleBlur}
          filterDate={filterDate}
          isClearable={clearable}
          placeholderText={placeholder}
          className={className}
          disabled={disabled}
          readOnly={disabled}
          minDate={minDate}
          maxDate={maxDate}
          dateFormat={dateFormat}
          showMonthDropdown={showMonthDropdown}
          showYearDropdown={showYearDropdown}
        />
        {enableTime ? (
          <MaterialTimePicker
            id={`${id}-timepicker`}
            className="material-time-picker"
            format="24hr"
            defaultTime={moment()
              .startOf('day')
              .toDate()}
            value={this.state.value.toDate()}
            disabled={disabled}
            onChange={(_, value) => {
              this.handleChange(moment(value))
            }}
          />
        ) : null}
      </div>
    )
  }

  renderEndDate() {
    const {
      id,
      clearable,
      placeholder,
      className,
      disabled,
      startDate,
      filterDate,
      minDate,
      maxDate,
      showMonthDropdown,
      showYearDropdown,
      dateFormat,
      enableTime,
      timeFormat,
      use12Hours
    } = this.props

    const dateTimePickerClass = classNames({
      'date-time-picker-wrapper': enableTime
    })

    return (
      <div className={dateTimePickerClass}>
        <DatePicker
          name={id}
          ref={id}
          selectsEnd
          startDate={startDate}
          endDate={this.state.value}
          selected={this.state.value}
          onChange={this.onDateChange}
          onBlur={this.handleBlur}
          filterDate={filterDate}
          isClearable={clearable}
          placeholderText={placeholder}
          className={className}
          disabled={disabled}
          readOnly={disabled}
          minDate={minDate}
          maxDate={maxDate}
          dateFormat={dateFormat}
          showMonthDropdown={showMonthDropdown}
          showYearDropdown={showYearDropdown}
        />
        {enableTime ? (
          <MaterialTimePicker
            id={`${id}-timepicker`}
            className="material-time-picker"
            format="24hr"
            value={this.state.value.toDate()}
            defaultTime={moment()
              .startOf('day')
              .toDate()}
            disabled={disabled}
            onChange={(_, value) => {
              this.handleChange(moment(value))
            }}
          />
        ) : null}
      </div>
    )
  }

  /**
   * On Date change
   *
   * Save time value from state if use
   * date picker with time picker.
   *
   * Because by default date picker does reset time value
   */
  onDateChange = val => {
    if (!this.props.enableTime) {
      return this.handleChange(val)
    }

    const value = this.getCheckedValue(val)

    const time = this.state.value || moment()

    const dateWithTime = moment(value).set({
      hours: time.hours(),
      minutes: time.minutes(),
      seconds: time.seconds()
    })

    this.handleChange(dateWithTime)
  }

  renderDate() {
    const {
      id,
      clearable,
      className,
      placeholder,
      disabled,
      filterDate,
      minDate,
      maxDate,
      dateFormat,
      enableTime,
      timeFormat,
      use12Hours,
      showMonthDropdown,
      showYearDropdown,
      wrapperClass,
      selectLightFormSkin
    } = this.props

    const pickerClass = classNames({
      'form-control': selectLightFormSkin,
      'form-control-danger': selectLightFormSkin && !this.state.isValid,
      [className]: !!className
    })

    const dateTimePickerClass = classNames({
      [wrapperClass]: !!wrapperClass,
      'date-time-picker-wrapper': enableTime
    })

    return (
      <div className={dateTimePickerClass}>
        <DatePicker
          name={id}
          ref={id}
          selected={this.state.value}
          onChange={this.onDateChange}
          onBlur={this.handleBlur}
          filterDate={filterDate}
          isClearable={clearable}
          placeholderText={placeholder}
          className={pickerClass}
          disabled={disabled}
          readOnly={disabled}
          minDate={minDate}
          maxDate={maxDate}
          dateFormat={dateFormat}
          showMonthDropdown={showMonthDropdown}
          showYearDropdown={showYearDropdown}
        />
        {enableTime ? (
          <MaterialTimePicker
            id={`${id}-timepicker`}
            className="material-time-picker"
            format="24hr"
            value={this.state.value.toDate()}
            defaultTime={moment()
              .startOf('day')
              .toDate()}
            disabled={disabled}
            onChange={(_, value) => {
              this.handleChange(moment(value))
            }}
          />
        ) : null}
      </div>
    )
  }

  render() {
    const { id, label, labelClass, helpText, selectsStart, selectsEnd, selectDarkSkin, selectLightFormSkin } = this.props

    const groupClass = classNames({
      'form-group': true,
      'has-danger': !this.state.isValid,
      'dark-picker': selectDarkSkin,
      'light-form-picker': selectLightFormSkin
    })

    const labelClassName = classNames({
      [labelClass]: !!labelClass
    })

    return (
      <fieldset className={groupClass} id={id}>
        {label && (
          <label className={labelClassName} htmlFor={id}>
            {label}
          </label>
        )}
        {selectsStart && this.renderStartDate()}
        {selectsEnd && this.renderEndDate()}
        {!selectsStart && !selectsEnd && this.renderDate()}
        {helpText && <small className="text-muted">{helpText}</small>}
        {this.state.errorMessage && <small className="text-danger">{this.state.errorMessage}</small>}
      </fieldset>
    )
  }
}



// WEBPACK FOOTER //
// src/components/UI/DatePickerInput.jsx